<template>
    <modal-dialog
        id="deltek-data"
        :show.sync="shown"
    >
        <template v-slot:title>
            {{ title }}
            <div v-if="!!last_import_date" class="uk-text-small">
                Last import: {{ last_import_date | nibnut.date }}
            </div>
        </template>
        <div class="uk-panel">
            <data-table
                :id="state_identifier"
                :columns="visible_columns"
                :rows="filtered_rows"
                :search="state.search"
                :total="state.total"
                :found="state.found"
                :current-page="state.page"
                :total-pages="nb_pages"
                :searchable="false"
                empty-title="No Deltek data available"
                @sort="sort_by"
                @page="goto_page"
            >
                <template
                    v-slot:tbody="{ row, field }"
                >
                    <span v-if="field === 'deltek_project_number'">{{ row.deltek_project_number }}</span>
                    <span v-else-if="field === 'hours'">{{ row.hours | nibnut.number("0,0.0") }}</span>
                    <span v-else-if="field.match(/^(fee|budget)$/i)">$&nbsp;{{ row[field] | nibnut.number("0,0") }}</span>
                    <span v-else-if="field === 'buttons'">
                        <base-button
                            size="small"
                            color="danger"
                            @click.prevent.stop="remove(row)"
                        >
                            <open-icon glyph="trash" />
                        </base-button>
                    </span>
                    <span v-else>{{ row[field] }}</span>
                </template>
            </data-table>
        </div>

        <div class="uk-modal-footer uk-text-center">
            <base-button size="large" :block="true" @click.native.prevent="close">Done</base-button>
        </div>
    </modal-dialog>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"
import { orderBy } from "lodash"

import is_remote_data_table_source from "../mixins/IsRemoteDataTableSource"

import ModalDialog from "./ModalDialog/ModalDialog"
import BaseButton from "../ui/BaseButton"
import OpenIcon from "../ui/OpenIcon"

export default {
    mixins: [is_remote_data_table_source],
    components: {
        ModalDialog,
        BaseButton,
        OpenIcon
    },
    watch: {
        show: "refresh",
        projectId: "refresh"
    },
    methods: {
        refresh () {
            if(this.show && this.projectId) {
                this.set_state_value("project_id", this.projectId)

                const query = {
                    ...this.state,
                    fields: this.fields,
                    relation_ids: this.relation_ids
                }

                this.loading = true
                this.$store.dispatch(
                    "FETCH_RECORDS",
                    {
                        entity: this.entity,
                        query
                    }
                ).then(response => {
                    this.current_records_ids = response.record_ids

                    this.set_state_values({
                        total: response.total || 0,
                        found: response.found || 0,
                        query_info: response.query_info || {}
                    })

                    this.post_load(response)
                }).catch(error => {
                    this.$error(error.message)
                }).then(() => {
                    this.loading = false
                })
            }
        },
        remove (record) {
            this.$confirm({
                type: "error",
                title: "Delete Deltek Phase Data",
                message: `Do you really want to delete Deltek phase ${record.deltek_phase_id}? Once gone, it will no longer count towards totals for the project. You can re-create this phase by including it in your next Deltek import.`,
                cancel: "Keep It",
                ok: "Delete It",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("RECORD_DELETE", {
                            entity: this.entity,
                            id: record.id
                        }).catch(error => {
                            if(error) this.$error(error.message)
                        })
                    }
                }
            })
        },
        close () {
            this.$emit("update:show", false)
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        state_identifier () {
            return "deltek-data"
        },
        shown: {
            get: function () {
                return this.show
            },
            set: function (show) {
                if(!show) this.close()
            }
        },
        fields () {
            const fields = Object.keys(this.columns)
            fields.push("project_id")
            fields.push("snapshot_date_as_string")
            return fields
        },
        raw_financial_records () {
            return this.entity_records("financial")
        },
        financial_records () {
            return orderBy(this.raw_financial_records, "deltek_phase_id", "asc")
        },
        deltek_project_number () {
            if(this.raw_financial_records.length) return this.raw_financial_records[0].deltek_project_number
            return ""
        },
        last_import_date () {
            if(this.raw_financial_records.length) return this.raw_financial_records[0].snapshot_date_as_string
            return ""
        },
        title () {
            let title = "Deltek Data"
            if(this.deltek_project_number) title += ` for project ${this.deltek_project_number.replace(/\.\d+/, "")}`
            return title
        }
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        projectId: {
            type: Number,
            required: true
        }
    },
    data () {
        return {
            entity: "financial",
            columns: {
                deltek_project_number: { label: "Scope", sort: null, type: "plain" },
                deltek_phase_id: { label: "Phase", sort: null, type: "plain" },
                hours: { label: "Hours", sort: null, type: "plain" },
                fee: { label: "Spent", sort: null, type: "plain" },
                budget: { label: "Fee", sort: null, type: "plain" },
                buttons: { label: " ", sort: false, type: "plain", cell_class: "lam-col-user-initials uk-text-right" }
            },

            default_state: {
                per_page: 10,
                page: 1,
                sort_by: "deltek_phase_id",
                sort_dir: "asc",
                filter_on: null,
                filter: "",
                archived: false,
                search: "",
                total: 0,
                found: 0,
                project_id: 0
            }
        }
    }
}
</script>
